import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Input, notification, Row, Col, Button, Modal, Breadcrumb, Typography, Spin, Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { formItemLayout, tailFormItemLayout, backButton, unexpectedError } from '../../common_functions';

const MovableWork = props => {
  const { section, id } = useParams();
  const [portfolio, setPortfolio] = useState({});
  const [loading, setLoading] = useState(false);
  const [supportData, setSupportData] = useState([]);
  const [allNumber, setAllNumber] = useState([]);
  const [number, setNumber] = useState([]);
  const [collections, setCollections] = useState([]);
  const [artist, setArtist] = useState([]);
  const [locations, setLocations] = useState([]);
  const formRef = useRef(null);
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        /**Get artist name*/
        let { data: artistFullName } = await props.getItems('Artist', {
          fields: ['id', 'First_Name', 'last_name'],
          sort: '-id',
          limit: -1,
        });
        setArtist(artistFullName);
        //get collection list
        await getCollections();
        //get support table data
        const { data: supData } = await props.getItems('support_table', {
          fields: ['id', 'type', 'name', 'fields'],
          limit: -1,
          filters: {
            fields: {
              _in: ['object_type', 'work_output', 'classification', 'catalog_level'],
            },
          },
        });
        setSupportData(supData);
        const { data: portNumber } = await props.getItems('portfolio', {
          fields: ['id', 'number', 'type', 'name', 'artist'],
          limit: -1,
          filters: { type: { _eq: process.env.conceptual_work_id } },
        });
        setAllNumber(portNumber);
        setNumber(portNumber);
        formRef.current.setFieldsValue({
          catalog_level: supData.find(el => el.fields === 'catalog_level')?.id || undefined,
          classification: supData.find(el => el.type === 'classification' && el.name.toLowerCase() === 'photograph')?.id || undefined,
          work_type: supData.find(el => el.fields === 'work_output' && el.name.toLowerCase() === 'positive')?.id || undefined,
          obj_type: supData.find(el => el.type === 'object type' && el.name.toLowerCase() === 'individual object')?.id || undefined,
        });
        if (!!id) {
          let { data } = await props.getItem('portfolio', id, {
            fields: [
              'id,name,artist,number,conceptual_number.id,conceptual_number.name,edition_number,collection,acquisition_date,recorded_size,catalog_level,classification,work_type,obj_type,location,provenance_notes',
            ],
          });
          getObjectLocationDetails(data.collection);
          showConceptualList(data.artist, portNumber);
          data = {
            ...data,
            acquisition_date: !!data.acquisition_date ? dayjs(data.acquisition_date) : null,
            published_date: !!data.published_date ? dayjs(data.published_date, 'YYYY-MM-DD') : null,
            name: data?.conceptual_number?.name,
            conceptual_number: data.conceptual_number.id,
          };
          setPortfolio(data);
          formRef?.current?.setFieldsValue(data);
        }
      } catch (error) {
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [section, id]);

  let onFinish = async values => {
    try {
      setLoading(true);
      let newState = {
        ...portfolio,
        ...values,
        type: process.env.movable_work_id,
      };
      let { data } = await props.saveItem('portfolio', newState);
      if (data) {
        notification['success']({
          message: 'Portfolio movable work saved successfully!',
          duration: 2,
        });
        backButton(props, `/portfolio-movable-work`);
      }
    } catch (error) {
      Modal.warning({
        title: 'Error',
        content: 'Not Saved!',
      });
    } finally {
      setLoading(false);
    }
  };

  let onFinishFailed = ({ errorFields }) => {
    formRef?.current?.scrollToField(errorFields[0].name);
  };
  // get collections list
  const getCollections = async () => {
    let { data: colRespData } = await props.getItems('collections', {
      fields: ['id', 'name'],
      sort: '-id',
      limit: -1,
    });
    setCollections(colRespData);
  };
  //In movable object screen show the conceptual object list according to artist selection

  const showConceptualList = (artistID, portNumber = []) => {
    formRef.current.setFieldsValue({ conceptual_number: '' });
    const portFolioData = allNumber?.length ? allNumber : portNumber;
    if (!portFolioData?.length) return;
    const filteredList = artistID ? portFolioData.filter(item => item.artist === artistID) : portFolioData;
    setNumber(filteredList);
    if (artistID) {
      if (portfolio.artist === artistID) {
        formRef.current.setFieldsValue({ conceptual_number: portfolio.conceptual_number });
      }
      if (filteredList.length === 0) {
        formRef.current.setFieldsValue({ conceptual_number: '', name: '' });
      }
    }
  };

  const getObjectLocationDetails = async collectionID => {
    setLocations([]);
    if (!!collectionID) {
      let { data: objectLocations } = await props.getItems('location', {
        fields: ['id', 'name'],
        sort: 'name',
        limit: -1,
        filters: { collections: { _eq: collectionID } },
      });
      setLocations(objectLocations);
    }
  };
  const setName = conceptualID => {
    if (!!conceptualID) {
      const result = allNumber.find(item => item.id === conceptualID);
      if (result) {
        formRef.current.setFieldsValue({ name: result.name });
      }
    }
  };
  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="app-breadcrumb-col">
          <Breadcrumb separator="/" items={[{ title: "Portfolio Movable Work", href: "/portfolio-movable-work" }, { title: !!id ? `Edit Portfolio Movable Work` : 'Add Portfolio Movable Work ' }]} />
        </Col>
        <Col className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {!!id ? `Edit Portfolio Movable Work: ${id}` : 'Add Portfolio Movable Work'}
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          <div className="artist">
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
            >
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="artist" label="Artist / Creator">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select artist / creator"
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                      onChange={showConceptualList}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {artist?.map(art => {
                        return <Select.Option key={art.id} value={art.id}>{art.First_Name + ' ' + art.last_name}</Select.Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="conceptual_number"
                    label="Number"
                    rules={[
                      {
                        required: true,
                        message: 'Portfolio number is required!',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select type"
                      optionFilterProp="children"
                      showSearch
                      allowClear
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                      onChange={setName}
                    >
                      {number?.map(item => (
                        <Select.Option key={item.id} value={item.id}>{item.number}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="edition_number" label="Edition / Set Number">
                    <Input placeholder="Enter Edition/Set number" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="name"
                    label="Name"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Name is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter Name" disabled={true} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="collection" label="Collection">
                    <Select
                      placeholder="Select collection"
                      style={{ textTransform: 'capitalize' }}
                      optionFilterProp="children"
                      onChange={getObjectLocationDetails}
                    >
                      {collections?.map(item => (
                        <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="acquisition_date" label="Collection Acquisition Date">
                    <DatePicker picker="year" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="provenance_notes" label="Provenance Notes">
                    <Input.TextArea rows={4} placeholder="Enter provenance notes..." />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="recorded_size" label="Recorded Size">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="location" label="Object Location Detail">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select Object Location"
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {locations?.map(data => {
                        return <Select.Option key={data.id} value={data.id}>{data.name}</Select.Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="catalog_level"
                    label="Catalog Level"
                    rules={[
                      {
                        required: true,
                        message: 'Catalog level is required!',
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select catalog level"
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    >
                      {supportData?.map(
                        el =>
                          el.fields === 'catalog_level' && (
                            <Select.Option key={el.id} value={el.id}>
                              {el.name}
                            </Select.Option>
                          )
                      )}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="classification"
                    label="Classification"
                    rules={[
                      {
                        required: true,
                        message: 'Classification is required!',
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select classification"
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    >
                      {supportData?.map(
                        el =>
                          el.type === 'classification' && (
                            <Select.Option key={el.id} value={el.id}>
                              {el.name}
                            </Select.Option>
                          )
                      )}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="work_type"
                    label="Work Type, Output"
                    rules={[
                      {
                        required: true,
                        message: 'Work type is required!',
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select work type"
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    >
                      {supportData?.map(
                        el =>
                          el.type === 'work type' &&
                          el.fields === 'work_output' && (
                            <Select.Option key={el.id} value={el.id}>
                              {el.name}
                            </Select.Option>
                          )
                      )}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="obj_type"
                    label="Object Type"
                    rules={[
                      {
                        required: true,
                        message: 'Object Type is required!',
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select object type"
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    >
                      {supportData?.map(
                        el =>
                          el.type === 'object type' && (
                            <Select.Option key={el.id} value={el.id}>
                              {el.name}
                            </Select.Option>
                          )
                      )}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={() => backButton(props, '/portfolio-movable-work')}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton(props.roleType, props.tabs_permissions.portfolio_movable_work, !!portfolio ? portfolio.id : '')}
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default MovableWork;
